import {
  getNavigationId,
  installClientConsoleCapture,
  reportRecoverableRenderError,
} from "@/logging/console.client";
import { useImpersonateStore } from "@/store/impersonateStore";
import { useShopDataStore } from "@/store/shopDataStore";
import { RemixBrowser } from "@remix-run/react";
import { startTransition, StrictMode } from "react";
import { hydrateRoot } from "react-dom/client";

// Before hydration, so a failure during hydration itself is captured. The root loader fills in
// the shop and the page request id afterwards; records written before that simply lack them,
// which is better than losing the record.
installClientConsoleCapture();

// Global fetch interceptor for customer support impersonation.
const originalFetch = window.fetch;
window.fetch = function (...args) {
  const [resource, config] = args;
  const requestResource = resource as unknown;

  // Get impersonated shop from store
  const impersonatedShop = useImpersonateStore.getState().impersonatedShop;

  // Skip adding headers for external requests to avoid CORS issues
  const resourceUrl =
    typeof resource === "string"
      ? resource
      : requestResource instanceof Request
        ? requestResource.url
        : String(resource);
  const isSameOrigin =
    resourceUrl.startsWith("/") ||
    resourceUrl.startsWith(window.location.origin);

  // The navigation id ties the several data requests of one page load together server-side.
  const navigationId = isSameOrigin ? getNavigationId() : undefined;
  const needsCustomHeaders = (impersonatedShop || navigationId) && isSameOrigin;
  if (!needsCustomHeaders) {
    return originalFetch(...args);
  }

  // Preserve existing headers from config or Request
  const existingHeaders =
    requestResource instanceof Request
      ? requestResource.headers
      : config?.headers;
  const headers = new Headers(existingHeaders);

  if (impersonatedShop) {
    headers.set("X-Impersonate-Shop", impersonatedShop);
  }
  if (navigationId) {
    headers.set("X-Navigation-Id", navigationId);
  }

  if (requestResource instanceof Request) {
    return originalFetch(new Request(requestResource, { headers }));
  }

  const newConfig = { ...(config ?? {}), headers };
  return originalFetch(resource, newConfig);
};

startTransition(() => {
  hydrateRoot(
    document,
    <StrictMode>
      <RemixBrowser />
    </StrictMode>,
    // Remix hydrates the whole document, so anything that changes it before React does — a browser
    // extension, a translation tool — fails hydration. React 18 recovers by re-rendering on the
    // client, and without this the only trace was the root ErrorBoundary logging a "failed" page
    // from the attempt React threw away.
    { onRecoverableError: reportRecoverableRenderError },
  );
});

// Rehydrate persisted store after React hydration to avoid SSR mismatch
useShopDataStore.persist.rehydrate();
